import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import { SearchProducts } from "../../utils/product";

const AdminAddProductToOrderDialog = ({
  open,
  onClose,
  onAdd,
}) => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] =
    useState(null);
  const [selectedOptionId, setSelectedOptionId] =
    useState("");
  const [amount, setAmount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const selectedProductName =
    selectedProduct?.name ??
    selectedProduct?.Name ??
    "";

  /*
    חיפוש מוצרים בשרת.

    החיפוש מתבצע רק כאשר:
    1. החלון פתוח.
    2. הוקלדו לפחות שתי אותיות.
    3. הטקסט אינו שם המוצר שכבר נבחר.
  */
  useEffect(() => {
    if (!open) {
      return;
    }

    const normalizedSearchTerm =
      searchTerm.trim();

    if (
      selectedProduct &&
      normalizedSearchTerm === selectedProductName
    ) {
      setLoading(false);
      return;
    }

    if (normalizedSearchTerm.length < 2) {
      setProducts([]);
      setLoading(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setLoading(true);
        setError("");

        const response = await SearchProducts(
          normalizedSearchTerm
        );

        const data =
          response?.data ??
          response;

        const loadedProducts =
          Array.isArray(data)
            ? data
            : data?.items ??
              data?.Items ??
              [];

        setProducts(loadedProducts);
      } catch (err) {
        console.error(
          "שגיאה בחיפוש מוצרים:",
          err
        );

        console.error(
          "תשובת השרת:",
          err?.response?.data
        );

        setProducts([]);
        setError(
          "לא הצלחנו לחפש מוצרים."
        );
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    searchTerm,
    open,
    selectedProduct,
    selectedProductName,
  ]);

const productOptions = useMemo(() => {
  return (
    selectedProduct?.options ??
    selectedProduct?.Options ??
    selectedProduct?.productOptions ??
    selectedProduct?.ProductOptions ??
    []
  );
}, [selectedProduct]);

  const selectedOption = useMemo(() => {
    return productOptions.find((option) => {
      const optionId =
        option?.id ??
        option?.Id;

      return (
        Number(optionId) ===
        Number(selectedOptionId)
      );
    });
  }, [
    productOptions,
    selectedOptionId,
  ]);

  const getProductPrice = () => {
    const productPrice = Number(
      selectedProduct?.price ??
        selectedProduct?.Price ??
        0
    );

    const optionPrice = Number(
      selectedOption?.extraPrice ??
        selectedOption?.ExtraPrice ??
        0
    );

    return optionPrice > 0
      ? optionPrice
      : productPrice;
  };

  const resetDialog = () => {
    setSelectedProduct(null);
    setSelectedOptionId("");
    setAmount(1);
    setSearchTerm("");
    setProducts([]);
    setError("");
    setLoading(false);
  };

  const handleClose = () => {
    resetDialog();
    onClose();
  };

  const handleProductChange = (
    _,
    product
  ) => {
    setSelectedProduct(product);
    setSelectedOptionId("");
    setAmount(1);
    setError("");

    if (!product) {
      setSearchTerm("");
      return;
    }

    const productName =
      product?.name ??
      product?.Name ??
      "";

    setSearchTerm(productName);

    /*
      משאירים ברשימה גם את המוצר שנבחר,
      כדי שה-Autocomplete יוכל לזהות אותו
      כחלק מה-options.
    */
    setProducts((currentProducts) => {
      const productId =
        product?.id ??
        product?.Id;

      const productAlreadyExists =
        currentProducts.some(
          (currentProduct) => {
            const currentProductId =
              currentProduct?.id ??
              currentProduct?.Id;

            return (
              Number(currentProductId) ===
              Number(productId)
            );
          }
        );

      if (productAlreadyExists) {
        return currentProducts;
      }

      return [
        product,
        ...currentProducts,
      ];
    });
  };

  const handleSearchInputChange = (
    _,
    newInputValue,
    reason
  ) => {
    if (reason === "input") {
      setSearchTerm(newInputValue);
      setError("");

      if (selectedProduct) {
        const currentSelectedProductName =
          selectedProduct?.name ??
          selectedProduct?.Name ??
          "";

        if (
          newInputValue !==
          currentSelectedProductName
        ) {
          setSelectedProduct(null);
          setSelectedOptionId("");
          setAmount(1);
        }
      }

      return;
    }

    if (reason === "clear") {
      setSearchTerm("");
      setProducts([]);
      setSelectedProduct(null);
      setSelectedOptionId("");
      setAmount(1);
      setError("");
      return;
    }

    if (reason === "reset") {
      setSearchTerm(newInputValue);
    }
  };

  const handleAdd = () => {
    if (!selectedProduct) {
      setError("יש לבחור מוצר.");
      return;
    }

    if (
      productOptions.length > 0 &&
      !selectedOptionId
    ) {
      setError(
        "יש לבחור אפשרות למוצר."
      );
      return;
    }

    const normalizedAmount =
      Number(amount);

    if (
      !Number.isInteger(
        normalizedAmount
      ) ||
      normalizedAmount <= 0
    ) {
      setError(
        "יש להזין כמות תקינה."
      );
      return;
    }

    const productId =
      selectedProduct?.id ??
      selectedProduct?.Id;

    if (!productId) {
      setError(
        "לא נמצא מזהה למוצר שנבחר."
      );
      return;
    }

    const productOptionId =
      selectedOption
        ? selectedOption?.id ??
          selectedOption?.Id
        : null;

    onAdd({
      id: 0,
      productId,
      productOptionId,
      amount: normalizedAmount,

      /*
        שומרים גם את האובייקטים עצמם,
        כדי שהמוצר יוצג מיד בטבלת ההזמנה.
      */
      product: selectedProduct,
      productOption:
        selectedOption ?? null,
    });

    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      dir="rtl"
      aria-labelledby="add-product-dialog-title"
    >
      <DialogTitle id="add-product-dialog-title">
        הוספת מוצר להזמנה
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: 1,
          }}
        >
          {error && (
            <Alert
              severity="error"
              role="alert"
            >
              {error}
            </Alert>
          )}

          <Autocomplete
            options={products}
            value={selectedProduct}
            inputValue={searchTerm}
            loading={loading}
            filterOptions={(options) =>
              options
            }
            onInputChange={
              handleSearchInputChange
            }
            onChange={
              handleProductChange
            }
            getOptionLabel={(product) =>
              product?.name ??
              product?.Name ??
              ""
            }
            isOptionEqualToValue={(
              option,
              value
            ) => {
              const optionId =
                option?.id ??
                option?.Id;

              const valueId =
                value?.id ??
                value?.Id;

              return (
                Number(optionId) ===
                Number(valueId)
              );
            }}
            noOptionsText={
              searchTerm.trim().length <
              2
                ? "יש להקליד לפחות 2 אותיות"
                : loading
                  ? "מחפש מוצרים..."
                  : "לא נמצאו מוצרים"
            }
            loadingText="מחפש מוצרים..."
            renderOption={(
              props,
              product
            ) => {
              const productId =
                product?.id ??
                product?.Id;

              const productName =
                product?.name ??
                product?.Name ??
                "";

              const productPrice =
                Number(
                  product?.price ??
                    product?.Price ??
                    0
                );

              return (
                <Box
                  component="li"
                  {...props}
                  key={productId}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems:
                      "flex-start",
                  }}
                >
                  <Typography>
                    {productName}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {productPrice.toLocaleString(
                      "he-IL",
                      {
                        style:
                          "currency",
                        currency: "ILS",
                      }
                    )}
                  </Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="חיפוש ובחירת מוצר"
                placeholder="הקלידי שם מוצר"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && (
                        <CircularProgress
                          size={20}
                        />
                      )}

                      {
                        params.InputProps
                          .endAdornment
                      }
                    </>
                  ),
                }}
              />
            )}
          />

          {selectedProduct &&
            productOptions.length >
              0 && (
              <TextField
                select
                label="בחירת אפשרות"
                value={
                  selectedOptionId
                }
                onChange={(event) => {
                  setSelectedOptionId(
                    event.target.value
                  );

                  setError("");
                }}
                required
              >
                {productOptions.map(
                  (option) => {
                    const id =
                      option?.id ??
                      option?.Id;

                    const optionType =
                      option?.optionType ??
                      option?.OptionType ??
                      "אפשרות";

                    const value =
                      option?.value ??
                      option?.Value ??
                      "";

                    const optionPrice =
                      Number(
                        option?.extraPrice ??
                          option?.ExtraPrice ??
                          0
                      );

                    return (
                      <MenuItem
                        key={id}
                        value={id}
                      >
                        {optionType}:{" "}
                        {value}

                        {optionPrice >
                          0
                          ? ` — ${optionPrice.toLocaleString(
                              "he-IL",
                              {
                                style:
                                  "currency",
                                currency:
                                  "ILS",
                              }
                            )}`
                          : ""}
                      </MenuItem>
                    );
                  }
                )}
              </TextField>
            )}

          <TextField
            type="number"
            label="כמות"
            value={amount}
            onChange={(event) => {
              setAmount(
                event.target.value
              );

              setError("");
            }}
            inputProps={{
              min: 1,
              step: 1,
            }}
            required
          />

          {selectedProduct && (
            <Typography>
              מחיר ליחידה:{" "}
              {getProductPrice().toLocaleString(
                "he-IL",
                {
                  style: "currency",
                  currency: "ILS",
                }
              )}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          ביטול
        </Button>

        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={
            loading ||
            !selectedProduct
          }
          sx={{
            backgroundColor:
              "#D81B60",
            "&:hover": {
              backgroundColor:
                "#ad1457",
            },
          }}
        >
          הוספה להזמנה
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminAddProductToOrderDialog;